import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import locale from "@/utils/locale";
import type { SupportedLanguage } from "./i18n.types";
import { env } from "@/config/env";
import utils from "../utils";

// 从环境变量读取配置
const envSupportedLanguages = window._config?.languageList
  ? window._config.languageList.map(lang => lang.id as SupportedLanguage)
  : (["en"] as SupportedLanguage[]);

const envDefaultLanguage = window._config?.lang
  ? (window._config.lang as SupportedLanguage)
  : ("en" as SupportedLanguage);

/**
 * 获取有效的语言列表
 * 优先使用 window._config.languageList，不存在则使用环境变量配置
 */
export const getValidLanguages = (): SupportedLanguage[] => {
  if (window._config?.languageList && window._config.languageList.length > 0) {
    return window._config.languageList.map(
      lang => lang.id as SupportedLanguage
    );
  }
  return envSupportedLanguages;
};

// 支持的语言列表 - 动态获取
export const SUPPORTED_LANGUAGES: SupportedLanguage[] = getValidLanguages();

// 默认语言
export const DEFAULT_LANGUAGE: SupportedLanguage = envDefaultLanguage;

// 从 localStorage 或 window._config 获取语言，使用优先级逻辑
export const getStoredLanguage = (): SupportedLanguage => {
  try {
    // 优先级1: 检查 localStorage 的 currentLanguage
    const storedLanguage = utils.getObject(
      "currentLanguage"
    ) as SupportedLanguage;
    if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage)) {
      return storedLanguage;
    }

    // 优先级2: 使用 window._config.lang
    if (
      window._config?.lang &&
      SUPPORTED_LANGUAGES.includes(window._config.lang as SupportedLanguage)
    ) {
      return window._config.lang as SupportedLanguage;
    }

    // 优先级3: 使用 window._config.languageList 第一条
    if (
      window._config?.languageList &&
      window._config.languageList.length > 0
    ) {
      const firstLang = window._config.languageList[0].id;
      if (SUPPORTED_LANGUAGES.includes(firstLang as SupportedLanguage)) {
        return firstLang as SupportedLanguage;
      }
    }

    // 优先级4: 使用国家对应的语言（如果 countryId 存在）
    const countryId = utils.getObject("countryId") as string;
    if (countryId && window._config?.countryList) {
      const country = window._config.countryList.find(c => c.id === countryId);
      if (
        country?.lang &&
        SUPPORTED_LANGUAGES.includes(country.lang as SupportedLanguage)
      ) {
        return country.lang as SupportedLanguage;
      }
    }

    // 优先级5: 使用默认语言
    return DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

// 保存语言到 localStorage
export const setStoredLanguage = (lang: SupportedLanguage): void => {
  try {
    utils.setObject("currentLanguage", lang);
  } catch (error) {
    console.error("Failed to store language:", error);
  }
};

const customMissingKeyHandler = (
  lng: readonly string[],
  ns: string,
  key: string,
  fallbackValue: string
): string => {
  const localeValue = locale.get(key);
  if (localeValue && localeValue !== key) {
    return localeValue;
  }

  // 否则返回 fallback 值
  return fallbackValue || key;
};

// 初始化 i18next
i18n
  .use(HttpBackend) // 使用 HTTP 后端进行懒加载
  .use(initReactI18next) // 集成 React
  .init({
    lng: getStoredLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,

    // 命名空间配置
    defaultNS: "translation",
    ns: ["translation"],

    // 懒加载配置
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },

    // 插值配置
    interpolation: {
      escapeValue: false, // React 已经处理了 XSS
    },

    // 使用自定义的 missing key handler
    saveMissing: false,
    missingKeyHandler: customMissingKeyHandler,

    // 解析配置
    parseMissingKeyHandler: (key: string) => {
      // 尝试从 locale.ts 获取
      const localeValue = locale.get(key);
      if (localeValue && localeValue !== key) {
        return localeValue;
      }
      return key;
    },

    // 性能优化
    react: {
      useSuspense: true, // 使用 Suspense 进行懒加载
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
    },

    debug: env.I18N_DEBUG,

    load: "languageOnly",
  });

i18n.on("languageChanged", (lng: string) => {
  if (SUPPORTED_LANGUAGES.includes(lng as SupportedLanguage)) {
    setStoredLanguage(lng as SupportedLanguage);
  }
});

export default i18n;
